/**
 * Is this failure a reason to destroy the session?
 *
 * ## Why this exists
 *
 * Every auth path here used to call `clearAllTokens()` on *any* error. That is wrong for
 * the same reason it was wrong on mobile: a network drop, a timeout, or a 5xx says
 * nothing about whether the member's credentials are valid. Only the server refusing
 * them does.
 *
 * It matters right now because the API is about to return **503** when Supabase is
 * unreachable, instead of the 401 it sends today. Without this split, a provider outage
 * would wipe every member's tokens at page load — the exact failure the API change
 * exists to prevent, just relocated.
 *
 * Safe to ship ahead of that API change: a 401 is still definitive, so nothing about
 * today's behaviour changes for a genuinely expired session.
 *
 * @module services/auth/api/sessionFailure
 */

/** Message fragments that mean a rejection when no status survived the throw. */
const REJECTION_PATTERNS = ['Unauthorized', 'Forbidden', 'invalid JWT', 'Authentication failed'];

/** Read an HTTP status off an error whatever shape it arrived in. */
export function statusOf(error: unknown): number | undefined {
  const e = error as { status?: number; message?: string } | undefined;
  if (typeof e?.status === 'number') return e.status;

  // `"<status>: <message>"`, the shape authService throws.
  const prefixed = /^(\d{3}):/.exec(e?.message ?? '')?.[1];
  return prefixed ? Number(prefixed) : undefined;
}

/**
 * True only when the server refused these credentials.
 *
 * Unknown-shaped errors return `false` — better to leave a session in place and let the
 * next request fail than to sign someone out over an error we couldn't read.
 */
export function isSessionRejected(error: unknown): boolean {
  const status = statusOf(error);
  if (status !== undefined) return status === 401 || status === 403;

  const message = (error as { message?: string } | undefined)?.message ?? '';
  return REJECTION_PATTERNS.some((pattern) => message.includes(pattern));
}
